import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, TrendingUp, Clock, FileCheck, Users, BarChart3, 
  DollarSign, Briefcase, RefreshCw, Heart, Award, ArrowRight, 
  CheckCircle2, FileText, BookOpen 
} from 'lucide-react';

const Landing = () => {
  return (
    <div className="marketing-layout">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-lg bg-slate-900/50 border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 md:px-12 py-3 sm:py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <img src="/logo.png" alt="ReserveRelease.AI" className="h-10 sm:h-14 md:h-16 object-contain" />
            <div className="flex flex-col">
              <span className="text-lg sm:text-2xl md:text-3xl font-bold whitespace-nowrap">
                <span className="bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-500 bg-clip-text text-transparent">
                  Reserve<span className="hidden xs:inline">Release</span>
                </span>
                <span className="text-white">.AI</span>
              </span>
              <span className="text-xs sm:text-sm text-slate-400 hidden sm:block">Reserve Recovery Solutions</span>
            </div>
          </Link>
          <div className="flex gap-1 sm:gap-4 items-center flex-shrink-0">
            <Link to="/reserve-education" className="text-white hover:text-purple-400 transition-colors text-xs sm:text-base px-2 sm:px-0">
              <span className="hidden sm:inline">Education</span>
              <BookOpen size={18} className="sm:hidden" />
            </Link>
            <Link to="/login" className="text-white hover:text-purple-400 transition-colors text-xs sm:text-base px-2 sm:px-0">
              Login
            </Link>
            <Link to="/register">
              <button className="primary-button text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3" data-testid="get-started-btn">
                <span className="hidden sm:inline">Get Started</span>
                <span className="sm:hidden">Start</span>
              </button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section pt-20 sm:pt-24 md:pt-20" data-testid="hero-section">
        <div className="hero-glow"></div>
        <div className="container mx-auto px-4 sm:px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8 items-center">
            <motion.div
              className="md:col-span-7 mt-4 sm:mt-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
                Unfreeze Your Capital.{' '}
                <span className="gradient-text">Unleash Your Growth.</span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-8 leading-relaxed">
                Recover frozen merchant reserves faster with AI-powered legal expertise. 
                Stop losing cash flow to payment processor holds.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                <Link to="/register">
                  <button className="primary-button text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 w-full sm:w-auto" data-testid="hero-cta-btn">
                    Start Recovery Now
                  </button>
                </Link>
                <button 
                  onClick={() => document.getElementById('merchant-reserves-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="secondary-button text-base sm:text-lg px-8 sm:px-10 py-4 sm:py-5 w-full sm:w-auto"
                >
                  Learn More
                </button>
              </div>
            </motion.div>
            <motion.div
              className="md:col-span-5 mt-8 md:mt-0"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.pexels.com/photos/6214470/pexels-photo-6214470.jpeg"
                alt="Stressed business owner with frozen funds"
                className="rounded-2xl shadow-2xl border border-white/10 w-full h-auto"
                style={{ filter: 'brightness(0.8)' }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                Payment Processors <span className="text-red-500">Freezing</span> Your Hard-Earned Revenue?
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                High-risk merchants lose millions annually to excessive reserve holds. Chargebacks, 
                compliance issues, or simple processor policies can lock away 10-30% of your revenue for months.
              </p>
              <ul className="space-y-4 text-slate-300">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-1 flex-shrink-0">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <span>Average freeze duration: <strong>90-180 days</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-1 flex-shrink-0">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <span>Typical hold amount: <strong>5-30% of processing volume</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-1 flex-shrink-0">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <span>Impact: Cash flow crisis, stunted growth, operational delays</span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="marketing-card p-8">
                <div className="text-6xl font-bold text-red-500 mb-4">$500B+</div>
                <p className="text-xl text-slate-300">
                  Lost annually by merchants to payment processing issues and reserve holds
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Merchant Reserves Education Section */}
      <section id="merchant-reserves-section" className="section-padding bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className="text-red-400">DON'T LET</span> <span className="gradient-text">Merchant Reserves</span> <span className="text-red-400">SHUT YOU DOWN</span>
            </h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto font-semibold">
              Your business survival depends on understanding these critical threats to your cash flow
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <motion.div
              className="marketing-card border-2 border-amber-500/30"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-amber-500/10 -m-8 p-6 mb-6 rounded-t-2xl border-b-2 border-amber-500/30">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-amber-300" size={24} />
                  </div>
                  The Reserve Fund Trap
                </h3>
              </div>
              <p className="text-slate-200 mb-4 text-lg font-semibold">
                <span className="text-amber-400">⚠️ CRITICAL:</span> Payment processors are legally draining your revenue through mandatory reserve holds.
              </p>
              <p className="text-slate-300 mb-4">
                Every business accepting credit cards faces <strong className="text-white">10-30% of processing volume withheld</strong> for <strong className="text-white">90-270 days*</strong>. This isn't optional—it's how processors protect themselves while YOUR capital sits frozen.
              </p>
              <div className="bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
                <p className="text-slate-200 text-sm">
                  <strong className="text-red-400">*High-Risk Businesses:</strong> Face reserves up to <strong>12 months</strong> with significantly higher hold percentages—sometimes exceeding 40% of monthly volume.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="marketing-card border-2 border-red-500/30"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-red-500/10 -m-8 p-6 mb-6 rounded-t-2xl border-b-2 border-red-500/30">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-red-500/30 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="text-red-300" size={24} />
                  </div>
                  VAMP Is Bankrupting Legitimate Businesses
                </h3>
              </div>
              <p className="text-slate-200 mb-4 text-lg font-semibold">
                <span className="text-red-400">🚨 URGENT:</span> Visa's Account Monitoring Program is destroying businesses across ALL industries.
              </p>
              <p className="text-slate-300 mb-4">
                VAMP's automated fraud algorithms don't care that you're legitimate. A sudden sales spike? Flagged. Industry you're in? Flagged. Customer disputes? <strong className="text-red-400">Account frozen instantly</strong>—no warning, no appeal process until it's too late.
              </p>
              <p className="text-slate-200 font-semibold">
                Result: <strong className="text-red-400">Thousands of businesses</strong> monthly lose access to funds, get MATCH-listed, and face permanent industry blacklisting—foreclosure and bankruptcy follow.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <motion.div
              className="marketing-card bg-gradient-to-br from-orange-900/30 to-red-900/30 border-2 border-orange-500/50"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold mb-4 text-orange-300">
                💥 The ACH-Reject Death Spiral
              </h3>
              <p className="text-slate-200 mb-4 text-lg font-semibold">
                <span className="text-orange-400">LOSING 50% MORE</span> to fees? You're caught in the ACH-Reject cycle.
              </p>
              <p className="text-slate-300 mb-4">
                <strong>Here's how it destroys your business:</strong>
              </p>
              <ol className="space-y-3 text-slate-300 mb-4">
                <li className="flex gap-3">
                  <span className="text-orange-400 font-bold flex-shrink-0">1.</span>
                  <span>Processor holds your reserves but <strong className="text-white">still charges processing fees</strong> on frozen funds</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-orange-400 font-bold flex-shrink-0">2.</span>
                  <span>When chargebacks occur, processor initiates <strong className="text-white">ACH debit from your bank</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="text-orange-400 font-bold flex-shrink-0">3.</span>
                  <span>Insufficient funds? <strong className="text-orange-400">ACH rejection triggers $25-50 fees</strong> per attempt</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-orange-400 font-bold flex-shrink-0">4.</span>
                  <span>Processor <strong className="text-white">retries automatically</strong>—racking up hundreds in rejection fees</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-orange-400 font-bold flex-shrink-0">5.</span>
                  <span>Your account gets flagged as "high-risk" triggering <strong className="text-red-400">EVEN HIGHER reserve requirements</strong></span>
                </li>
              </ol>
              <div className="bg-red-900/30 border border-red-500/50 p-4 rounded-lg">
                <p className="text-slate-200 font-semibold">
                  <strong className="text-red-400">The trap:</strong> Each ACH rejection compounds—you're paying fees on top of fees while your reserves stay frozen longer. Many merchants lose <strong className="text-white">50-75% MORE</strong> than their original chargeback amount.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Bank Vault Cracked Open with Currency Explosion */}
              <div className="relative overflow-visible rounded-2xl border-2 border-purple-500/50 mb-6 bg-gradient-to-br from-purple-900/20 to-slate-900 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1645380192916-cf0057dbc339?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxiYW5rJTIwdmF1bHQlMjBzYWZlJTIwZG9vciUyMG9wZW4lMjBmcm9udCUyMHZpZXclMjBzZWN1cml0eSUyMG1ldGFsfGVufDB8fHx8MTc2NTgzODA4M3ww&ixlib=rb-4.1.0&q=85"
                  alt="Bank vault cracked open - reserverelease.AI releasing frozen reserves"
                  className="w-full h-auto"
                />
                
                {/* Enhanced Currency Symbol Explosion Effect - More Visible */}
                <div className="absolute inset-0 pointer-events-none overflow-visible">
                  {/* Longer strands of animated currency symbols */}
                  {['$', '€', '¥', '£', '₹', '₿', '$', '€', '¥', '£', '₹', '₿', '$', '€', '¥', '£', '₹', '₿', '$', '€', '¥', '£', '₹', '₿', '$', '€', '¥', '£', '₹', '₿', '$', '€'].map((symbol, index) => (
                    <div
                      key={index}
                      className="absolute font-bold animate-currency-burst"
                      style={{
                        left: '50%',
                        top: '50%',
                        fontSize: index % 4 === 0 ? '3.5rem' : index % 4 === 1 ? '3rem' : index % 4 === 2 ? '2.5rem' : '2rem',
                        color: index % 3 === 0 ? '#FFD700' : index % 3 === 1 ? '#10B981' : '#06B6D4',
                        textShadow: '0 0 30px currentColor, 0 0 60px currentColor, 0 0 90px currentColor',
                        animation: `currencyBurst${index % 16} 4s ease-out infinite`,
                        animationDelay: `${index * 0.1}s`,
                        opacity: 0,
                        fontWeight: '900'
                      }}
                    >
                      {symbol}
                    </div>
                  ))}
                  
                  {/* Enhanced glowing effect from vault center */}
                  <div className="absolute inset-0 bg-gradient-radial from-cyan-400/30 via-transparent to-transparent mix-blend-screen"></div>
                  <div className="absolute inset-0 bg-gradient-radial from-amber-400/20 via-transparent to-transparent mix-blend-screen"></div>
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-purple-900/60"></div>
              </div>
              
              {/* AI Agent Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-purple-600 to-cyan-600 rounded-full p-4 shadow-2xl border-4 border-white animate-pulse z-10">
                <div className="text-center">
                  <div className="text-2xl mb-1">🤖</div>
                  <div className="text-white text-xs font-bold whitespace-nowrap">RR AI Agent</div>
                </div>
              </div>
              <div className="marketing-card bg-gradient-to-br from-purple-900/30 to-cyan-900/30 border-2 border-purple-500/50">
                <h3 className="text-2xl font-bold mb-4 text-purple-300">
                  ✅ STOP THE BLEEDING. STAY OPEN. RECOVER FAST.
                </h3>
                <p className="text-slate-200 mb-4 text-lg">
                  <strong className="text-green-400">You DON'T have to accept this.</strong>
                </p>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 text-xl flex-shrink-0">✓</span>
                    <span><strong className="text-white">Avoid foreclosure</strong> by recovering frozen capital before it's too late</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 text-xl flex-shrink-0">✓</span>
                    <span><strong className="text-white">Break the ACH-reject cycle</strong> with strategic legal intervention</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 text-xl flex-shrink-0">✓</span>
                    <span><strong className="text-white">Return to positive revenue</strong> within weeks, not months</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 text-xl flex-shrink-0">✓</span>
                    <span><strong className="text-white">Protect against future VAMP attacks</strong> across all verticals—retail, e-commerce, hospitality, professional services</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          <div className="marketing-card bg-slate-950 border-2 border-purple-500/50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border-l-4 border-purple-500 pl-6">
                <div className="text-3xl font-bold text-purple-400 mb-2">90-270 Days*</div>
                <div className="text-slate-300">Standard reserve holding period</div>
                <div className="text-xs text-slate-500 mt-1">*High-risk: up to 365 days</div>
              </div>
              <div className="border-l-4 border-cyan-500 pl-6">
                <div className="text-3xl font-bold text-cyan-400 mb-2">10-30%*</div>
                <div className="text-slate-300">Percentage of revenue withheld</div>
                <div className="text-xs text-slate-500 mt-1">*High-risk: up to 40%+</div>
              </div>
              <div className="border-l-4 border-red-500 pl-6">
                <div className="text-3xl font-bold text-red-400 mb-2">+50%</div>
                <div className="text-slate-300">Additional loss from ACH-reject fees</div>
                <div className="text-xs text-slate-500 mt-1">Compounds with each rejection</div>
              </div>
            </div>
          </div>

          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-purple-900/50 to-cyan-900/50 border-2 border-purple-500/50 rounded-2xl p-8 max-w-4xl mx-auto">
              <p className="text-xl text-slate-200 mb-6 font-semibold">
                <span className="text-green-400 text-2xl">💡 THE SOLUTION:</span> These holds ARE negotiable. Wrongful freezes CAN be challenged. ACH cycles CAN be broken.
              </p>
              <p className="text-lg text-slate-300">
                With expert legal strategy and AI-powered case analysis, merchants recover frozen funds <strong className="text-white">50% faster</strong>, avoid bankruptcy, and return to positive cash flow—while staying protected from future processor attacks.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Businesses Need This Service */}
      <section className="section-padding bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Why <span className="gradient-text">E-Commerce Merchants</span> Choose Us
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              Facing the financial strain of held funds? We provide a specialized, technology-driven solution 
              to unlock your capital and return liquidity to your balance sheet months ahead of schedule.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <motion.div
              className="bg-gradient-to-br from-green-900/40 to-emerald-900/20 border border-green-500/30 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                <DollarSign className="text-green-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Accelerated Access to Capital</h3>
              <p className="text-slate-400 text-sm">
                Return liquidity to your balance sheet months ahead of schedule. Our automated analysis proves 
                long hold periods (often 180+ days) are unnecessary.
              </p>
            </motion.div>

            <motion.div
              className="bg-gradient-to-br from-blue-900/40 to-cyan-900/20 border border-blue-500/30 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                <Users className="text-blue-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Professional Advocacy</h3>
              <p className="text-slate-400 text-sm">
                We navigate complex processor bureaucracy using industry-specific language and legal frameworks. 
                We have the experience and knowledge to provide you with solutions.
              </p>
            </motion.div>

            <motion.div
              className="bg-gradient-to-br from-purple-900/40 to-pink-900/20 border border-purple-500/30 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                <BarChart3 className="text-purple-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Data-Driven Risk Mitigation</h3>
              <p className="text-slate-400 text-sm">
                Our proprietary AI analyzes transaction data, chargeback ratios, and fulfillment patterns to create 
                a comprehensive "Risk Mitigation Report" proving excessive reserves.
              </p>
            </motion.div>

            <motion.div
              className="bg-gradient-to-br from-amber-900/40 to-orange-900/20 border border-amber-500/30 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                <ShieldCheck className="text-amber-400" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Success-Based Compensation</h3>
              <p className="text-slate-400 text-sm">
                No upfront financial risk. We only earn a fee when successfully securing your fund release—aligning 
                our interests directly with your recovery goals.
              </p>
            </motion.div>
          </div>

          {/* Additional Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <Clock className="text-cyan-400 mb-4" size={24} />
              <h4 className="font-bold text-white mb-2">Significant Time Savings</h4>
              <p className="text-slate-400 text-sm">
                We handle the entire reserve dispute process through Limited Power of Attorney, 
                allowing you to focus on operations.
              </p>
            </motion.div>

            <motion.div
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <TrendingUp className="text-green-400 mb-4" size={24} />
              <h4 className="font-bold text-white mb-2">Prevent Future Holds</h4>
              <p className="text-slate-400 text-sm">
                Our AI analysis provides strategic insights into specific triggers, helping you optimize 
                processing behavior and avoid future reserves.
              </p>
            </motion.div>

            <motion.div
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              viewport={{ once: true }}
            >
              <FileText className="text-purple-400 mb-4" size={24} />
              <h4 className="font-bold text-white mb-2">Transparent Communication</h4>
              <p className="text-slate-400 text-sm">
                Clear roadmap, regular updates, and a dedicated point of contact—demystifying 
                the status of your capital unlike opaque processors.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ISO & Agent Partnership Section */}
      <section className="section-padding bg-gradient-to-b from-slate-900 via-cyan-900/10 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 rounded-full px-4 py-2 mb-6">
                <Briefcase className="text-cyan-400" size={18} />
                <span className="text-cyan-400 font-semibold text-sm">PARTNERSHIP OPPORTUNITY</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-text">ISOs & Agents:</span> Create New Revenue Streams
              </h2>
              
              <p className="text-lg text-slate-300 mb-8">
                Partner with Reserve Release AI to unlock a powerful new income source. Turn "dead" leads into revenue, 
                enhance merchant retention, and differentiate yourself from competitors with our AI-powered solution.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="text-green-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">Referral Fees on Every Recovery</h4>
                    <p className="text-slate-400 text-sm">Earn compensation for every merchant successfully boarded and for funds recovered.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="text-purple-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">Monetize "Dead" Leads</h4>
                    <p className="text-slate-400 text-sm">Generate revenue from former clients or terminated merchants with funds in "Reserve Prison."</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <Heart className="text-cyan-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">Enhanced Merchant Retention</h4>
                    <p className="text-slate-400 text-sm">Become the "problem solver" that resolves their biggest pain point—building deep, lasting loyalty.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Award className="text-amber-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">Competitive Differentiation</h4>
                    <p className="text-slate-400 text-sm">Offer a unique selling proposition with high-tech AI positioning that sets you apart.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <Briefcase className="text-cyan-400" size={28} />
                  ISO Partnership Benefits
                </h3>

                <div className="space-y-6">
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-700/50">
                    <h4 className="font-bold text-green-400 mb-2 flex items-center gap-2">
                      <TrendingUp size={18} />
                      New Revenue Streams
                    </h4>
                    <ul className="text-slate-400 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Referral fees for successful fund recovery</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Monetize terminated merchant relationships</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Income beyond traditional merchant processing</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-700/50">
                    <h4 className="font-bold text-purple-400 mb-2 flex items-center gap-2">
                      <ShieldCheck size={18} />
                      Operational Efficiency
                    </h4>
                    <ul className="text-slate-400 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
                        <span>Offload complex reserve disputes to specialists</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
                        <span>AI-powered analysis saves time and resources</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
                        <span>Compliance-aligned negotiations reduce risk</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-700/50">
                    <h4 className="font-bold text-cyan-400 mb-2 flex items-center gap-2">
                      <Users size={18} />
                      Strengthen Client Relationships
                    </h4>
                    <ul className="text-slate-400 text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span>End-to-end support builds significant trust</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span>Merchants return for future processing needs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span>Data insights for portfolio risk management</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section-padding">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 md:order-1"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.pexels.com/photos/5673488/pexels-photo-5673488.jpeg"
                alt="Successful handshake deal"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
            <motion.div
              className="order-1 md:order-2"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-text">AI-Powered</span> Reserve Recovery
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                reserverelease.AI combines legal expertise with artificial intelligence to accelerate 
                the recovery of frozen merchant funds. We analyze your case, negotiate with processors, 
                and secure faster releases.
              </p>
              <div className="grid grid-cols-1 gap-4">
                <div className="marketing-card flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-purple-400" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">50% Faster Recovery</h3>
                    <p className="text-slate-400">AI analyzes your processing data to predict optimal release timelines</p>
                  </div>
                </div>
                <div className="marketing-card flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="text-cyan-400" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Legal Expertise</h3>
                    <p className="text-slate-400">Proven strategies from payment processing litigation specialists</p>
                  </div>
                </div>
                <div className="marketing-card flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-green-400" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">95% Success Rate</h3>
                    <p className="text-slate-400">Track record of successful reserve releases and negotiations</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">How It Works</h2>
            <p className="text-xl text-slate-300">Simple, transparent, and effective</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: <FileCheck size={32} />,
                title: 'Upload Your Data',
                description: 'Submit processing statements and freeze documentation. Our AI extracts key insights instantly.'
              },
              {
                step: '02',
                icon: <BarChart3 size={32} />,
                title: 'AI Analysis',
                description: 'Advanced algorithms analyze your case, predict release timelines, and identify optimal negotiation strategies.'
              },
              {
                step: '03',
                icon: <Users size={32} />,
                title: 'Expert Recovery',
                description: 'Our legal team negotiates with processors while you track progress in real-time through your dashboard.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="marketing-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-6xl font-bold text-purple-500/20 mb-4">{item.step}</div>
                <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center mb-6 text-purple-400">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-slate-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-purple-900 to-indigo-900">
        <div className="container mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Recover Your Reserves?
            </h2>
            <p className="text-lg sm:text-xl text-slate-200 mb-8 max-w-2xl mx-auto">
              Join hundreds of merchants who've successfully recovered frozen funds with reserverelease.AI
            </p>
            <Link to="/register">
              <button className="primary-button text-base sm:text-lg md:text-xl px-8 sm:px-10 md:px-12 py-4 sm:py-5 md:py-6 glow-effect" data-testid="cta-register-btn">
                Start Your Recovery Today
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-8 sm:py-12 border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center text-slate-400">
            <p className="mb-2">&copy; 2025 ReserveRelease.AI. All rights reserved.</p>
            <div className="flex justify-center gap-6 mb-4">
              <Link to="/" className="text-purple-400 hover:text-purple-300">Home</Link>
              <Link to="/reserve-education" className="text-purple-400 hover:text-purple-300">Education Center</Link>
              <Link to="/login" className="text-purple-400 hover:text-purple-300">Client Portal</Link>
            </div>
            <p className="text-sm mb-6">Specialized in merchant reserve recovery and payment processing litigation.</p>
            
            <div className="border-t border-white/10 pt-6 mt-6">
              <p className="text-xs text-slate-500 max-w-4xl mx-auto leading-relaxed">
                <strong className="text-slate-400">DISCLAIMER:</strong> The information on this website is for general information purposes only. Nothing on this site should be taken as legal advice for any individual case or situation. This information is not intended to create, and receipt of viewing does not constitute, an attorney client relationship. Prior results do not guarantee a similar outcome.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
